import { FC, FormEventHandler, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Loader } from 'components';
import { TextField } from 'components/inputs/text-field';
import ErrorDialog from 'components/organization/dialog/error-dialog';
import useLocationApi from 'hooks/use-location-api';
import { Location } from 'types';

import 'components/inputs/date/lockable.scss';
import StateSelect from '../state';
import TimeZoneSelect from '../time-zone';

const CreateLocation: FC = () => {
  const navigate = useNavigate();
  const { createLocation, validateCodeUniqueness } = useLocationApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [location, setLocation] = useState<Partial<Location>>({});

  const handleReset: FormEventHandler = useCallback(
    e => {
      e.preventDefault();
      navigate(`/settings/locations`, { replace: true });
    },
    [navigate]
  );

  const handleSubmit: FormEventHandler = useCallback(
    async e => {
      e.preventDefault();
      setLoading(true);
      if (!location) return;
      const result = await createLocation(location);
      if (result) navigate(`/settings/locations/${result.id}`, { replace: true });
      setLoading(false);
    },
    [createLocation, location, navigate]
  );

  return (
    <Loader loading={loading}>
      <form
        className="page"
        id="create-location"
        onReset={handleReset}
        onSubmit={handleSubmit}
        data-cy="new-location-form"
      >
        <div className="page-header">
          <h1>NEW LOCATION</h1>
          <div className="grid-2">
            <button className="btn-outline" type="reset">
              CANCEL
            </button>
            <button className="btn-primary" type="submit">
              SUBMIT
            </button>
          </div>
        </div>
        <div className="card-primary">
          <div className="card-header mb-5" />
          <div className="card-body">
            <div className="grid-3 p-2" style={{ maxWidth: 1200 }}>
              <TextField
                id="location-name"
                name="name"
                label="NAME"
                value={location.name ?? ''}
                onChange={name => setLocation(prevState => ({ ...prevState, name }))}
                required
                allowSpaces
                data-cy="new-location-name-input"
              />
              <TextField
                id="location-code"
                name="code"
                label="CODE"
                value={location.code ?? ''}
                onChange={code => setLocation(prevState => ({ ...prevState, code }))}
                validate={validateCodeUniqueness}
                required
                data-cy="new-location-code-input"
              />
              <TimeZoneSelect
                timeZone={location.timeZone}
                setTimeZone={timeZone => setLocation(prevState => ({ ...prevState, timeZone }))}
              />
              <TextField
                id="location-address"
                name="address"
                label="ADDRESS 1"
                value={location.address ?? ''}
                onChange={address => setLocation(prevState => ({ ...prevState, address }))}
                required
                allowSpaces
                data-cy="new-location-address-input"
              />
              <TextField
                id="location-address-2"
                name="address2"
                label="ADDRESS 2"
                value={location.address2 ?? ''}
                onChange={address2 => setLocation(prevState => ({ ...prevState, address2 }))}
                allowSpaces
                data-cy="new-location-address-2-input"
              />
              <TextField
                id="location-zip-code"
                name="zipCode"
                label="ZIP CODE"
                inputMode="numeric"
                value={location.zipCode ?? ''}
                onChange={zipCode => setLocation(prevState => ({ ...prevState, zipCode }))}
                required
                data-cy="new-location-zip-code-input"
              />
              <TextField
                id="location-city"
                name="city"
                label="CITY"
                value={location.city ?? ''}
                onChange={city => setLocation(prevState => ({ ...prevState, city }))}
                required
                allowSpaces
                data-cy="new-location-city-input"
              />
              <StateSelect
                state={location.state}
                setState={state => setLocation(prevState => ({ ...prevState, state }))}
              />
              <TextField
                id="location-country"
                name="country"
                label="COUNTRY"
                value={location.country ?? ''}
                onChange={country => setLocation(prevState => ({ ...prevState, country }))}
                required
                allowSpaces
                data-cy="new-location-country-input"
              />
              <TextField
                id="location-remit-to"
                name="remitTo"
                label="REMIT TO"
                value={location?.remitTo ?? ''}
                onChange={remitTo => setLocation(prevState => ({ ...prevState, remitTo }))}
                allowSpaces
                data-cy="new-location-remit-to-input"
              />
              <div className="text-field">
                <input
                  type="checkbox"
                  checked={!!location.invoiceRemitToDisplayed}
                  onChange={e =>
                    setLocation(prevState => ({ ...prevState, invoiceRemitToDisplayed: e.target.checked }))
                  }
                  style={{ margin: '8px 16px 0', outline: 'none', width: 16 }}
                  data-cy="new-location-remit-to-checkbox"
                />
                <label>INVOICE REMIT TO</label>
              </div>
            </div>
          </div>
        </div>
      </form>
      <ErrorDialog />
    </Loader>
  );
};

export default CreateLocation;
